import { useTranslate } from '@refinedev/core';
import {
  Card,
  Descriptions,
  Flex,
  Rate,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { PublicationListOut, ReviewOut } from '@/api/generated/schemas';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ShowLink } from '@/components/common/ShowLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ReviewStatusTag } from '@/components/feedback/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';

const { Paragraph, Text } = Typography;

interface ReviewGeneralTabProps {
  record: ReviewOut;
  publication?: PublicationListOut;
}

export const ReviewGeneralTab = ({
  record,
  publication,
}: ReviewGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();

  const emotions = record.emotions ?? [];

  const basicInfoItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'publication',
      label: t('feedback.reviews.fields.publication_id'),
      children: orDash(
        record.publication_id ? (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        ) : null
      ),
    },
    {
      key: 'profile',
      label: t('feedback.reviews.fields.profile_id'),
      children: orDash(<ProfileIconLink profileId={record.profile_id} />),
    },
    {
      key: 'author_name',
      label: t('feedback.reviews.fields.author_name'),
      children: orDash(record.author_name),
    },
    {
      key: 'rating',
      label: t('feedback.reviews.fields.rating'),
      children: <Rate allowHalf disabled value={record.rating} />,
    },
    {
      key: 'emotions',
      label: t('feedback.reviews.fields.emotion_ids'),
      children: orDash(
        emotions.length > 0 ? (
          <Space size={4} wrap>
            {emotions.map((emotion) => (
              <Tag key={emotion.id} style={{ marginInlineEnd: 0 }}>
                {emotion.name}
              </Tag>
            ))}
          </Space>
        ) : null
      ),
    },
    {
      key: 'comment',
      label: t('feedback.reviews.fields.comment'),
      span: 3,
      children: orDash(
        record.comment ? (
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {record.comment}
          </Paragraph>
        ) : null
      ),
    },
  ];

  const moderationItems: DescriptionsProps['items'] = [
    {
      key: 'status',
      label: t('feedback.reviews.fields.status'),
      children: <ReviewStatusTag record={record} />,
    },
    {
      key: 'accepted_at',
      label: t('feedback.reviews.fields.accepted_at'),
      children: orDash(formatDateTime(record.accepted_at)),
    },
    {
      key: 'rejected_at',
      label: t('feedback.reviews.fields.rejected_at'),
      children: orDash(formatDateTime(record.rejected_at)),
    },
    {
      key: 'rejection_reason',
      label: t('feedback.reviews.fields.rejection_reason'),
      children: orDash(
        record.rejection_reason
          ? t(`feedback.reviews.rejection_reasons.${record.rejection_reason}`)
          : null
      ),
    },
    {
      key: 'reported_at',
      label: t('feedback.reviews.fields.reported_at'),
      children: orDash(formatDateTime(record.reported_at)),
    },
    {
      key: 'reported_by',
      label: t('feedback.reviews.fields.reported_by'),
      children: orDash(
        record.reported_by != null ? (
          <ShowLink resource="account.profiles" id={record.reported_by} />
        ) : null
      ),
    },
    {
      key: 'reason',
      label: t('feedback.reviews.fields.reason'),
      span: 3,
      children: orDash(record.reason),
    },
  ];

  const systemInfoItems: DescriptionsProps['items'] = [
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={basicInfoItems}
        />
      </Card>
      <Card title={t('feedback.reviews.sections.moderation')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={moderationItems}
        />
      </Card>
      <Card title={t('base.sections.system_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={systemInfoItems}
        />
      </Card>
    </Flex>
  );
};
